import { create, StateCreator } from "zustand";
import { devtools } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";

import { ConfigStoreState, createConfigSlice } from "./configStore";
import { createDependencySlice, DependencyStoreState } from "./dependencyStore";
import { createFolderSlice, FolderStoreState } from "./folderStore";
import { createGraphSlice, GraphStoreState } from "./graphStore";
import { createImageSlice, ImageStoreState } from "./imageStore";
import { createItemSlice, ItemStoreState } from "./itemStore";
import { createOptionPairSlice, OptionPairStoreState } from "./optionPairStore";
import { createOptionSlice, OptionStoreState } from "./optionStore";
import { createProductSlice, ProductStoreState } from "./productStore";
import { createStageSlice, StageStoreState } from "./stageStore";
import {
   ConfiguratorStoreState,
   createMainConfiguratoreSlice,
} from "./mainConfiguratorStore";
import fetchDb from "@/utils/customRequestFunction";
import { CategoryStoreState, categoryStoreSlice } from "./categoryStore";
import { LearnMoreStore, createLearnMoreSlice } from "./learnMoreStore";
type WithDevtools = [["zustand/devtools", never]];

export type AdminSliceCreator<SliceState extends object> = StateCreator<
   AdminStoreState,
   WithDevtools,
   [],
   SliceState
>;

// Combined store state type
export type AdminStoreState = ProductStoreState &
   StageStoreState &
   OptionStoreState &
   ItemStoreState &
   ImageStoreState &
   DependencyStoreState &
   ConfigStoreState &
   FolderStoreState &
   GraphStoreState &
   OptionPairStoreState &
   ConfiguratorStoreState &
   CategoryStoreState &
   LearnMoreStore;

export const useAdminStore = create<AdminStoreState>()(
   devtools((set, get, api) => ({
      ...createProductSlice(set, get, api),
      ...createStageSlice(set, get, api),
      ...createOptionSlice(set, get, api),
      ...createItemSlice(set, get, api),
      ...createImageSlice(set, get, api),
      ...createConfigSlice(set, get, api),
      ...createFolderSlice(set, get, api),
      ...createDependencySlice(set, get, api),
      ...createGraphSlice(set, get, api),
      ...createOptionPairSlice(set, get, api),
      ...createMainConfiguratoreSlice(set, get, api),
      ...categoryStoreSlice(set, get, api),
      ...createLearnMoreSlice(set, get, api),
      addProduct: async (customName?: string) => {
         try {
            // Generate a unique name if not provided
            const productName =
               customName || `New Product ${uuidv4().substring(0, 8)}`;

            const response = await fetchDb.post("/api/main-product", {
               name: productName,
               imageId: null,
               title: productName,
               initialConstants: {},
               relativePos: 0,
               hide: false,
               stages: [],
            });

            if (response) {
               await get().fetchProducts();
               set({ selectedProductId: response.id });
            }

            return response;
         } catch (error) {
            console.error("Error adding product:", error);
            // If there's a name conflict, try again with a different UUID
            if (error.response?.status === 409) {
               return get().addProduct();
            }
            throw error;
         }
      },
   }))
);
